/**
 * `pdfTemplates.*` — Settings ▸ Document templates manager (ADR-0021).
 *
 * Company-scoped CRUD over `pdf_templates`. Flint's built-in defaults
 * (`isBuiltIn`) are read-only; operators duplicate-to-customise and pick
 * which template is the active default per kind ("attach").
 *
 * Preview is NOT here — it streams PDF bytes via the Hono route
 * `POST /api/pdf-templates/preview`, not tRPC JSON.
 */
import { z } from "zod";
import { uuid } from "../_common.js";

export const templateKind = z.enum([
  "invoice",
  "credit_note",
  "receipt",
  "bill",
  "payment_voucher",
  "petty_cash_voucher",
  "transfer_voucher",
  "purchase_order",
  "goods_receipt",
]);
export type TemplateKind = z.infer<typeof templateKind>;

const MAX_HTML = 300_000;

const templateRow = z.object({
  id: uuid,
  kind: templateKind,
  name: z.string(),
  htmlTemplate: z.string(),
  isDefault: z.boolean(),
  /** company_id IS NULL — a Flint built-in default (read-only). */
  isBuiltIn: z.boolean(),
});
export type PdfTemplateRow = z.infer<typeof templateRow>;

export const listTemplatesInput = z.object({});
export const listTemplatesOutput = z.object({ templates: z.array(templateRow) });

export const createTemplateInput = z.object({
  kind: templateKind,
  name: z.string().trim().min(1).max(120),
  htmlTemplate: z.string().min(1).max(MAX_HTML),
  makeDefault: z.boolean().optional(),
});
export const createTemplateOutput = z.object({ id: uuid });

export const updateTemplateInput = z.object({
  id: uuid,
  name: z.string().trim().min(1).max(120).optional(),
  htmlTemplate: z.string().min(1).max(MAX_HTML).optional(),
});
export const updateTemplateOutput = z.object({ id: uuid });

export const setDefaultTemplateInput = z.object({ id: uuid });
export const setDefaultTemplateOutput = z.object({ ok: z.boolean() });

export const deleteTemplateInput = z.object({ id: uuid });
export const deleteTemplateOutput = z.object({ ok: z.boolean() });
